import * as React from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { auth } from '../config/firebase';

const AuthContext = React.createContext(null);

export const useAuth = () => {
  return React.useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = React.useState(null);

  /**
   * Firebase is the single source of truth for who is signed in.
   *
   * The signed-out branch used to do nothing, so `user` was only ever cleared
   * by handleLogout. A session that ended any other way — an expired or revoked
   * token, a sign-out in another tab, credentials deleted server-side — left
   * this context reporting a user that Firebase had already dropped. Every
   * `useAuth().user` check then passed while the underlying requests failed on
   * rules, which reads to a traveller as the app breaking rather than as being
   * signed out.
   *
   * `setUser(null)` on the initial callback is free: `user` already starts null,
   * so React bails out of the re-render.
   */
  React.useEffect(() => {
    // The returned unsubscribe was previously dropped, so each remount of the
    // provider stacked another listener on the same auth instance.
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(
        firebaseUser
          ? {
              uid: firebaseUser.uid,
              displayName: firebaseUser.displayName,
              email: firebaseUser.email,
            }
          : null
      );
    });

    return unsubscribe;
  }, []);

  const handleLogin = async ({ email, password }) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log("user details ", user);
      return userCredential;
    } catch (error) {
      console.log("user error ", error);
      throw error; // Re-throw so the caller can catch it and display a toast
    }
  };

  const handleLogout = () => {
    // `setUser(null)` is not repeated here — the observer above fires on
    // sign-out and clears it. Doing both would be two renders and, worse, two
    // places that decide what "signed out" means.
    // Logged rather than swallowed. NOT rethrown: the only caller
    // (Header/index.js) invokes this from an onClick without awaiting, so a
    // rejection here would surface as an unhandled promise rejection instead
    // of anything useful. Telling the traveller it failed needs a toast at the
    // call site, which is a separate change.
    return signOut(auth).catch((error) => {
      console.error('Sign-out failed:', error);
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        onLogin: handleLogin,
        onLogout: handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

